import {
  APPOINTMENT_DEFAULT_DEPARTURE_MINUTES,
  APPOINTMENT_DEFAULT_TRAVEL_MINUTES,
  APPOINTMENT_DEFAULT_WORK_MINUTES,
} from '../../../entities/appointment';
import {
  ACTIVE_SEGMENT_KIND,
  BOARD_START_HOUR,
  BOARD_TOTAL_MINUTES,
  applyDurationDrag,
  clampMoveDelta,
  formatBoardMinutes,
  formatDuration,
  parseDayIso,
  slotDayIso,
  snapMinutes,
  xPxToMinutes,
  type Durations,
  type MinuteRange,
  type PreviewLayout,
  type ScheduleSlot,
  type SegmentHandle,
} from '../../../shared/lib/schedule';
import type { DispositionModeName } from './useDispositionMode';

/**
 * The draft block a planning click leaves on the board. Pure, no React.
 *
 * A draft is drawn as a `PreviewLayout`, which means `DragGhost` draws it and `detectDragCollision`
 * decides whether it is red — the same two pieces the drag work already built. There is no second
 * ghost, and `applyDraftDrag` runs through `applyDurationDrag` — the same one rule a real block's
 * resize commits through — so the draft cannot behave differently from the block it will become.
 */

/** The placed-but-not-saved block: one technician, one snapped start, three live durations. */
export interface PlanningDraftState {
  technicianId: number;
  /** Minutes from `BOARD_START_HOUR`, snapped, of the block's **start** (travel included). */
  startMin: number;
  durations: Durations;
}

/**
 * A draft the board arrives already holding, rather than one a click creates.
 *
 * Rescheduling seeds it from the Termin being moved, so the draft opens standing exactly where that
 * Termin is — same technician, same start, same three durations.
 */
export interface PlanningSeed {
  /** Identity of what is being placed, e.g. `appointment:42`. A new subject re-seeds. */
  key: string;
  /** The seed only applies while the board is showing this day. */
  dayIso: string;
  state: PlanningDraftState;
}

/**
 * What a fresh draft is born with, per mode. The appointment row reads the same constants
 * `getDefaultValues` does, so the board's draft and the modal cannot disagree.
 *
 * These are *create* defaults. A move must not use them — carrying a four-hour Termin over would
 * silently propose sixty minutes of work — so `usePlanningDraft` prefers a `PlanningSeed`'s
 * durations whenever one is present.
 */
export const DRAFT_DEFAULTS: Record<DispositionModeName, Durations> = {
  'schedule-appointment': {
    travel: APPOINTMENT_DEFAULT_TRAVEL_MINUTES,
    work: APPOINTMENT_DEFAULT_WORK_MINUTES,
    departure: APPOINTMENT_DEFAULT_DEPARTURE_MINUTES,
  },
  'schedule-task': { travel: 0, work: 60, departure: 0 },
};

export const totalMinutes = (d: Durations): number => d.travel + d.work + d.departure;

/** The board range the draft occupies — what the collision check and the drag primitive read. */
export const draftSpan = (draft: PlanningDraftState): MinuteRange => ({
  startMin: draft.startMin,
  endMin: draft.startMin + totalMinutes(draft.durations),
});

/**
 * A click inside a row track → a fresh draft. Snapping matches the drag's 15-minute grid so a
 * planned block lands where a dragged one would; a click too close to 22:00 clamps the start back
 * into the window instead of being swallowed — with a two-hour appointment draft, "nothing
 * happened" would be the response to most of the late afternoon.
 */
export function pickFromOffsetPx(
  offsetPx: number,
  technicianId: number,
  defaults: Durations,
): PlanningDraftState {
  const snapped = Math.max(0, snapMinutes(xPxToMinutes(offsetPx)));
  const startMin = Math.min(snapped, Math.max(0, BOARD_TOTAL_MINUTES - totalMinutes(defaults)));
  return { technicianId, startMin, durations: { ...defaults } };
}

/**
 * One drag gesture applied to the draft — the planning twin of `buildSchedulePatch`, except the
 * outcome is new state instead of a request. `move` is clamped to the drawn window exactly like a
 * real block's move; resizes are left unclamped, also like a real block's.
 */
export function applyDraftDrag(
  draft: PlanningDraftState,
  handle: 'move' | SegmentHandle,
  deltaMinutes: number,
  technicianId: number,
): PlanningDraftState {
  if (handle === 'move') {
    const clamped = clampMoveDelta(deltaMinutes, draftSpan(draft));
    return { ...draft, technicianId, startMin: draft.startMin + clamped };
  }

  const next = applyDurationDrag(draft.durations, handle, deltaMinutes);
  return {
    ...draft,
    technicianId,
    startMin: draft.startMin + next.startDelta,
    durations: next.durations,
  };
}

/**
 * The draft as a ghost layout. Colours come from the caller, so this stays free of the board's
 * colour tables. `activeHandle` dims the segments a resize is not touching — same treatment the
 * drag ghost gives a real block.
 */
export function draftLayout(
  draft: PlanningDraftState,
  colors: { workColor: string; travelColor: string },
  activeHandle: 'move' | SegmentHandle | null = null,
): PreviewLayout {
  const { durations, startMin } = draft;
  const activeKind = activeHandle ? ACTIVE_SEGMENT_KIND[activeHandle] : null;

  const label = (kind: SegmentHandle, minutes: number) =>
    // Mirrors `buildPreviewLayout`: on a move the work strip announces the new clock time.
    activeHandle === 'move' && kind === 'work'
      ? formatBoardMinutes(startMin + durations.travel)
      : formatDuration(minutes);

  return {
    startMin,
    endMin: startMin + totalMinutes(durations),
    segments: (['travel', 'work', 'departure'] as const).map((kind) => ({
      kind,
      minutes: durations[kind],
      color: kind === 'work' ? colors.workColor : colors.travelColor,
      label: label(kind, durations[kind]),
      active: activeKind == null || activeKind === kind,
    })),
  };
}

/**
 * The pick as a `<input type="datetime-local">` value, for the form's `plannedStart`.
 *
 * **`plannedStart` is the work start, not the block start.** The draft's live travel is added here —
 * forgetting it would file every planned appointment `travelMinutes` too early, which is exactly the
 * trap `appointmentForm.schema.ts` warns about at the top.
 */
export function slotToDatetimeLocal(
  dayIso: string,
  startMin: number,
  travelMinutes: number,
): string {
  const day = parseDayIso(dayIso);
  if (!day) return '';

  const workStart = new Date(day);
  workStart.setMinutes(BOARD_START_HOUR * 60 + startMin + travelMinutes, 0, 0);

  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${workStart.getFullYear()}-${pad(workStart.getMonth() + 1)}-${pad(workStart.getDate())}` +
    `T${pad(workStart.getHours())}:${pad(workStart.getMinutes())}`
  );
}

/**
 * What the upsert modals open with. Structurally matches both `UpsertDefaultSlot` and
 * `UpsertTaskDefaultSlot` — deliberately not their named types, so this feature never imports the
 * upsert features. The keys are the RHF field names, which is what lets the modals spread the slot
 * straight over their form defaults.
 *
 * This is the **single** draft → form conversion point; the travel arithmetic of
 * `slotToDatetimeLocal` must never be repeated anywhere else.
 */
export function draftToDefaultSlot(draft: PlanningDraftState, dayIso: string): ScheduleSlot {
  return {
    assignedTechnicianId: draft.technicianId,
    plannedStart: slotToDatetimeLocal(dayIso, draft.startMin, draft.durations.travel),
    plannedTravelDuration: draft.durations.travel,
    plannedWorkDuration: draft.durations.work,
    plannedDepartureDuration: draft.durations.departure,
  };
}

/**
 * The inverse: what the form currently says, as a block on the board.
 *
 * This is what makes the live preview possible — the modal reports a `ScheduleSlot` on every
 * keystroke and the board draws it through the very same `draftLayout` / `detectDragCollision` pair a
 * picked slot goes through, so a typed time and a dragged one cannot render differently.
 *
 * **The travel is subtracted again here**, mirroring `slotToDatetimeLocal`: `plannedStart` is the
 * work start and a drawn block begins `plannedTravelDuration` earlier.
 *
 * `null` — nothing to draw — when the slot has no technician, when `plannedStart` is empty or
 * half-typed, when a duration is not a finite number (an emptied `<input type="number">` yields
 * `NaN`), when the slot lands on a different day than the board is showing, or when it falls
 * entirely outside the drawn window. That last rule is `toBoardSpan`'s: a 23:00 Termin is not drawn
 * for an existing entry either, and drawing one only for the block being typed would put it in a
 * part of the row that does not exist.
 *
 * A slot that merely *overhangs* an edge is kept, and the geometry is deliberately **unclamped**,
 * like `buildPreviewLayout`: a block whose Anfahrt starts at 05:45 keeps a negative `startMin` and
 * is cut to the window at draw time.
 */
export function slotToDraft(slot: ScheduleSlot, dayIso: string): PlanningDraftState | null {
  if (!slot.assignedTechnicianId) return null;
  if (slotDayIso(slot) !== dayIso) return null;
  if (!parseDayIso(dayIso)) return null;

  const durations: Durations = {
    travel: slot.plannedTravelDuration,
    work: slot.plannedWorkDuration,
    departure: slot.plannedDepartureDuration,
  };
  if (!Object.values(durations).every(Number.isFinite)) return null;

  const time = /T(\d{2}):(\d{2})/.exec(slot.plannedStart);
  if (!time) return null;

  const workStartMin = Number(time[1]) * 60 + Number(time[2]) - BOARD_START_HOUR * 60;
  const startMin = workStartMin - durations.travel;

  if (startMin + totalMinutes(durations) <= 0 || startMin >= BOARD_TOTAL_MINUTES) return null;

  return { technicianId: slot.assignedTechnicianId, startMin, durations };
}

/**
 * The same slot on another day: the date part of `plannedStart` is replaced, the clock time and the
 * durations stay. This is what "switch the day while a form is docked" means — the Termin goes along
 * to the new day instead of being left behind on the old one. A half-typed `plannedStart` is returned
 * unchanged, because there is no time to carry over.
 */
export function withSlotDay(slot: ScheduleSlot, dayIso: string): ScheduleSlot {
  const time = /T(\d{2}:\d{2})/.exec(slot.plannedStart);
  if (!time) return slot;
  return { ...slot, plannedStart: `${dayIso}T${time[1]}` };
}

/** German label for what is being planned, for the bar's heading and its button. */
export const PLAN_LABELS: Record<
  DispositionModeName,
  { noun: string; action: string; move: string }
> = {
  'schedule-appointment': {
    noun: 'Termin',
    action: 'Termin anlegen',
    move: 'Termin verschieben',
  },
  'schedule-task': { noun: 'Aufgabe', action: 'Aufgabe planen', move: 'Aufgabe verschieben' },
};
